import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const weekday = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return ["日", "月", "火", "水", "木", "金", "土"][d.getDay()];
};

const formatQty = (n) => {
  const v = Number(n || 0);
  return v % 1 === 0 ? v.toFixed(0) : v.toFixed(2);
};

const fmtShort = (dateStr) => {
  if (!dateStr) return "";
  const p = String(dateStr).split("-");
  if (p.length !== 3) return dateStr;
  return `${parseInt(p[1], 10)}/${parseInt(p[2], 10)}`;
};

// 自由記載欄のヘッダー（空欄=手書き用）
const FREE_COLS = 3;
const ROWS_PER_PAGE = 16;

// 1日分の発注控えシート（A4・改page対応）
function DaySheet({ storeName, storeCode, day, isLast }) {
  const items = day.items || [];
  const padCount = Math.max(0, ROWS_PER_PAGE - items.length);
  return (
    <div
      className="day-sheet bg-white shadow-sm mx-auto mb-6 print:shadow-none print:mb-0"
      style={{
        width: "210mm",
        minHeight: "297mm",
        padding: "15mm 14mm",
        boxSizing: "border-box",
        color: "#1e293b",
        fontFamily: '"Hiragino Kaku Gothic ProN", "Yu Gothic", Meiryo, sans-serif',
        breakAfter: isLast ? "auto" : "page",
        pageBreakAfter: isLast ? "auto" : "always",
      }}
      data-testid={`store-print-day-${day.usage_date}`}
    >
      {/* ヘッダー */}
      <div className="flex items-end justify-between border-b-2 border-slate-800 pb-2 mb-4">
        <div>
          <div className="text-xs text-slate-500">発注控え</div>
          <h1 className="text-2xl font-bold tracking-wide">使用日別発注書</h1>
        </div>
        <div className="text-right text-xs text-slate-500">
          発行日: {new Date().toLocaleDateString("ja-JP")}
        </div>
      </div>

      {/* メタ情報 */}
      <table className="w-full text-sm mb-4 border border-slate-300">
        <tbody>
          <tr className="border-b border-slate-300">
            <th className="bg-slate-100 text-left px-3 py-2 w-28 border-r border-slate-300">
              施設名
            </th>
            <td className="px-3 py-2 font-medium">
              {storeName}{" "}
              <span className="text-slate-500 text-xs">({storeCode})</span>
            </td>
            <th className="bg-slate-100 text-left px-3 py-2 w-28 border-l border-r border-slate-300">
              使用日
            </th>
            <td className="px-3 py-2 font-medium">
              {day.usage_date} ({weekday(day.usage_date)})
            </td>
          </tr>
          <tr>
            <th className="bg-slate-100 text-left px-3 py-2 border-r border-slate-300">
              発注先
            </th>
            <td className="px-3 py-2" colSpan={3}>
              野菜の丸平（株式会社フレッシュエージェント） 03-3855-1610
            </td>
          </tr>
        </tbody>
      </table>

      {/* 明細 */}
      <table className="w-full text-sm border border-slate-300 mb-4">
        <thead>
          <tr className="bg-slate-100 border-b border-slate-300">
            <th className="px-2 py-2 text-left w-8 border-r border-slate-300">#</th>
            <th className="px-2 py-2 text-left w-20 border-r border-slate-300">納品日</th>
            <th className="px-2 py-2 text-left border-r border-slate-300">商品名</th>
            <th className="px-2 py-2 text-right w-24 border-r border-slate-300">数量</th>
            <th className="px-2 py-2 text-left w-14 border-r border-slate-300">単位</th>
            {Array.from({ length: FREE_COLS }).map((_, i) => (
              <th
                key={`fh-${i}`}
                className={`px-2 py-2 w-20 ${i < FREE_COLS - 1 ? "border-r border-slate-300" : ""}`}
              >
                &nbsp;
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => (
            <tr key={idx} className="border-b border-slate-200">
              <td className="px-2 py-1.5 text-slate-500 border-r border-slate-200">
                {idx + 1}
              </td>
              <td className="px-2 py-1.5 text-slate-700 border-r border-slate-200">
                {fmtShort(it.delivery_date)}
              </td>
              <td className="px-2 py-1.5 border-r border-slate-200">
                <span className={`font-medium ${it.cancelled ? "line-through text-slate-400" : ""}`}>{it.product_name}</span>
                {it.cancelled && (
                  <span className="ml-1 text-[10px] px-1 py-0.5 bg-red-100 text-red-700 rounded-sm font-bold border border-red-300">
                    取消
                  </span>
                )}
                {it.product_code && (
                  <span className="text-xs text-slate-500 ml-1">({it.product_code})</span>
                )}
                {it.is_other && (
                  <span className="ml-1 text-[10px] px-1 py-0.5 bg-amber-100 text-amber-800 rounded-sm">
                    リスト外
                  </span>
                )}
                {it.notes && (
                  <div className="text-xs text-slate-500 mt-0.5">備考: {it.notes}</div>
                )}
              </td>
              <td className={`px-2 py-1.5 text-right font-mono border-r border-slate-200 ${it.cancelled ? "line-through text-slate-400" : ""}`}>
                {formatQty(it.quantity)}
              </td>
              <td className={`px-2 py-1.5 border-r border-slate-200 ${it.cancelled ? "line-through text-slate-400" : ""}`}>{it.unit}</td>
              {Array.from({ length: FREE_COLS }).map((_, i) => (
                <td
                  key={`fc-${idx}-${i}`}
                  className={`px-2 py-1.5 ${i < FREE_COLS - 1 ? "border-r border-slate-200" : ""}`}
                >
                  &nbsp;
                </td>
              ))}
            </tr>
          ))}
          {/* 余白行（A4を埋める用） */}
          {Array.from({ length: padCount }).map((_, i) => (
            <tr key={`pad-${i}`} className="border-b border-slate-200">
              <td className="px-2 py-1.5 text-slate-300 border-r border-slate-200">&nbsp;</td>
              <td className="px-2 py-1.5 border-r border-slate-200">&nbsp;</td>
              <td className="px-2 py-1.5 border-r border-slate-200">&nbsp;</td>
              <td className="px-2 py-1.5 border-r border-slate-200">&nbsp;</td>
              <td className="px-2 py-1.5 border-r border-slate-200">&nbsp;</td>
              {Array.from({ length: FREE_COLS }).map((_, j) => (
                <td
                  key={`pf-${i}-${j}`}
                  className={`px-2 py-1.5 ${j < FREE_COLS - 1 ? "border-r border-slate-200" : ""}`}
                >
                  &nbsp;
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* 連絡事項（顧客からの連絡事項） */}
      {day.comments && (
        <div
          className="border border-slate-300 rounded-sm px-3 py-2 mb-4 text-sm"
          data-testid={`store-print-comments-${day.usage_date}`}
        >
          <span className="font-semibold text-slate-700">連絡事項:</span>{" "}
          <span className="text-slate-700 whitespace-pre-wrap">{day.comments}</span>
        </div>
      )}

      {/* 押印欄（右下に2つ） */}
      <div className="flex justify-end mt-6">
        <div className="flex gap-3">
          <div className="w-24 h-24 border border-slate-400 rounded-sm" />
          <div className="w-24 h-24 border border-slate-400 rounded-sm" />
        </div>
      </div>
    </div>
  );
}

export default function StorePrintPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const today = new Date(Date.now() + 9 * 3600 * 1000).toISOString().split("T")[0];
  const initial = searchParams.get("date") || today;
  const [startDate, setStartDate] = useState(initial);
  const [endDate, setEndDate] = useState(initial);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async (s, e) => {
    if (!s || !e) return;
    if (s > e) {
      toast.error("開始日は終了日より前にしてください");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/orders/print/store-range?start=${s}&end=${e}`,
        getAuthHeaders()
      );
      setData(res.data);
    } catch (err) {
      toast.error("印刷データの取得に失敗しました");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchData(startDate, endDate);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExcel = () => {
    if (!data || !(data.days || []).length) return;
    const wb = XLSX.utils.book_new();
    const usedNames = {};
    (data.days || []).forEach((day) => {
      const rows = [];
      rows.push([`発注控え`]);
      rows.push([`施設名`, `${data.store_name || ""}（${data.store_code || ""}）`]);
      rows.push([`使用日`, `${day.usage_date}（${weekday(day.usage_date)}）`]);
      if (day.comments) rows.push([`連絡事項`, day.comments]);
      rows.push([]);
      rows.push(["納品日", "商品コード", "商品名", "数量", "単位", "備考", "取消"]);
      (day.items || []).forEach((it) => {
        rows.push([
          fmtShort(it.delivery_date),
          it.product_code || "",
          it.product_name || "",
          Number(it.quantity ?? 0),
          it.unit || "",
          it.notes || "",
          it.cancelled ? "取消" : "",
        ]);
      });
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = [
        { wch: 8 }, { wch: 12 }, { wch: 32 }, { wch: 8 }, { wch: 6 }, { wch: 20 }, { wch: 6 },
      ];
      // シート名: 使用日（Excel禁止文字回避・31文字上限・重複回避）
      let base = String(day.usage_date).replace(/[\\/?*[\]:]/g, "-").slice(0, 31);
      let name = base;
      let n = 2;
      while (usedNames[name]) { name = `${base.slice(0, 28)}_${n++}`; }
      usedNames[name] = true;
      XLSX.utils.book_append_sheet(wb, ws, name);
    });
    const fname = `発注控え_${data.store_name || data.store_code || "施設"}_${data.start}_${data.end}.xlsx`;
    XLSX.writeFile(wb, fname);
    toast.success("Excelをダウンロードしました");
  };

  const days = data?.days || [];
  const totalItems = days.reduce((s, d) => s + (d.items?.length || 0), 0);

  // 控えシート（プレビューと印刷ポータルで共用）
  const sheetEls = days.map((day, i) => (
    <DaySheet
      key={day.usage_date}
      storeName={data.store_name}
      storeCode={data.store_code}
      day={day}
      isLast={i === days.length - 1}
    />
  ));

  return (
    <div className="min-h-screen bg-slate-100 p-6 print:bg-white print:p-0">
      {/* ツールバー（印刷時は非表示） */}
      <div className="no-print max-w-4xl mx-auto mb-4 flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          data-testid="store-print-back"
          className="h-9"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          戻る
        </Button>
        <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-2">
          <label className="text-sm text-slate-700">使用日:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            data-testid="store-print-start"
            className="h-9 px-2 border border-slate-300 rounded-sm text-sm"
          />
          <span className="text-slate-500">〜</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            data-testid="store-print-end"
            className="h-9 px-2 border border-slate-300 rounded-sm text-sm"
          />
          <Button type="submit" data-testid="store-print-fetch" className="h-9">
            表示
          </Button>
        </form>
        <Button
          onClick={handlePrint}
          disabled={!days.length || loading}
          data-testid="store-print-button"
          className="h-9 ml-auto bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <Printer className="h-4 w-4 mr-1" />
          印刷 / PDF保存
        </Button>
        <Button
          onClick={handleExcel}
          disabled={!days.length || loading}
          data-testid="store-print-excel"
          variant="outline"
          className="h-9 border-emerald-600 text-emerald-700 hover:bg-emerald-50"
        >
          <FileSpreadsheet className="h-4 w-4 mr-1" />
          Excelダウンロード
        </Button>
      </div>

      {/* 件数サマリ（印刷時は非表示） */}
      {!loading && data && (
        <div
          className="no-print max-w-4xl mx-auto mb-3 text-sm text-slate-600"
          data-testid="store-print-summary"
        >
          {days.length > 0 ? (
            <>
              期間 {data.start} 〜 {data.end}：使用日 <b>{days.length}</b> 日 / 合計{" "}
              <b>{totalItems}</b> 明細（使用日ごとに1ページで保存できます）
            </>
          ) : (
            <>この期間に発注はありません</>
          )}
        </div>
      )}

      {/* A4プレビュー（画面表示のみ。印刷はページ下部のポータルで行う） */}
      <div id="store-print-root" data-testid="store-print-sheet" className="no-print">
        {loading && (
          <div className="text-center text-slate-500 py-10">読み込み中...</div>
        )}
        {!loading && sheetEls}
        {!loading && data && days.length === 0 && (
          <div className="bg-white shadow-sm mx-auto p-10 text-center text-slate-400 max-w-2xl rounded">
            この期間に発注はありません
          </div>
        )}
      </div>

      {/* 印刷専用ポータル: レイアウト外(body直下)に控えを描画し、確実に印刷する */}
      {!loading &&
        days.length > 0 &&
        createPortal(
          <div className="print-portal" aria-hidden="true">
            {sheetEls}
          </div>,
          document.body
        )}

      {/* 印刷用CSS: 印刷はbody直下のポータルのみ出力し、アプリ本体(#root)は隠す */}
      <style>{`
        .print-portal { display: none; }
        @media print {
          @page { size: A4 portrait; margin: 0; }
          html, body {
            margin: 0 !important;
            background: #fff !important;
            height: auto !important;
            overflow: visible !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          #root { display: none !important; }
          .print-portal { display: block !important; }
          .no-print { display: none !important; }
          .day-sheet {
            width: 100% !important;
            min-height: 0 !important;
            height: auto !important;
            box-shadow: none !important;
            margin: 0 !important;
            padding: 10mm 12mm !important;
            page-break-after: always;
            break-after: page;
            break-inside: avoid;
          }
          .print-portal .day-sheet:last-child {
            page-break-after: auto;
            break-after: auto;
          }
          .day-sheet th, .day-sheet td {
            padding-top: 2px !important;
            padding-bottom: 2px !important;
            line-height: 1.25 !important;
          }
        }
      `}</style>
    </div>
  );
}
